// The canonical map from a TanStack query to what a screen shows —
// loading (skeleton) / error / empty / disabled / ready — through the
// shared contract (`resolveLoadStatus`, `@bitacorapro/shared/loading`).
// It replaces the `if (isLoading || !data)` pattern, which conflates
// "loading" with "nothing to show" and hangs on a skeleton forever when
// the query is disabled (`enabled: false` → isLoading stays false but
// data never comes).
//
// Cache-first: data in the cache renders at once, with no skeleton; a
// background refetch never changes the kind. A refetch that fails over
// kept data stays `ready` with `refreshFailed` — the page never blanks.
//
// A first-load error stays on screen through automatic refetches (window
// focus, reconnect, invalidation): TanStack v5 resets an errored query
// with no data back to `status: "pending"` / `error: null` the moment a
// refetch starts, which would flip the error back to a skeleton the user
// never asked for. Only `retry` (the user's Reintentar) brings the
// skeleton back — the shared contract's "automatic retry keeps the
// error" row. The held error is tied to the query's `errorUpdatedAt`, so
// a new query key (a fresh query with no error of its own) never
// inherits another key's error.
//
// Offline: a query with no data whose fetch is `paused` (TanStack's
// networkMode waiting for a connection) is an `error` with
// `offline: true`, not a skeleton that never ends.
import {
  isSkeletonVisible,
  resolveLoadStatus,
  REVALIDATING_HINT_MS,
  SKELETON_SLOW_MS,
} from "@bitacorapro/shared/loading";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useSkeletonPhase } from "./useSkeletonPhase";

export type QueryView<T, E = Error> =
  | { kind: "loading"; visible: boolean; slow: boolean }
  | { kind: "error"; error: E | null; offline: boolean; retry: () => void }
  | { kind: "empty" }
  | { kind: "disabled" }
  | { kind: "ready"; data: T; refreshFailed: boolean; revalidatingHint: boolean };

export type QueryViewOptions<T> = {
  /** Data that loaded but has nothing to show → `empty`, not `ready`. */
  isEmpty?: (data: T) => boolean;
  /**
   * The queries this one's `enabled` depends on — pass the WHOLE
   * ancestor chain (for C ← B ← A, C passes [A, B]), not just the
   * parent. While one of them is fetching, a disabled query is still
   * `loading`; an ancestor that is itself disabled (pending but idle)
   * is not on its way, so it doesn't count. Once none is fetching, a
   * query that stayed disabled is `disabled`, never a skeleton forever.
   */
  waitingOn?: UseQueryResult<unknown, unknown>[];
};

/** True once `active` has held for `ms` without a break. */
function useHeldFor(active: boolean, ms: number): boolean {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!active) {
      setHeld(false);
      return;
    }
    const t = setTimeout(() => setHeld(true), ms);
    return () => clearTimeout(t);
  }, [active, ms]);
  return active && held;
}

export function useQueryView<T, E = Error>(
  q: UseQueryResult<T, E>,
  opts?: QueryViewOptions<T>
): QueryView<T, E> {
  const hasData = q.data !== undefined;
  const offline = !hasData && q.fetchStatus === "paused";
  const fetching = q.isPending && q.fetchStatus === "fetching";
  // isPending + idle = the query is disabled. An ancestor that is
  // actually fetching means this one will be enabled once it lands; an
  // ancestor that is itself disabled is not on its way.
  const disabled = q.isPending && q.fetchStatus === "idle";
  const upstreamPending = (opts?.waitingOn ?? []).some(
    (w) => w.isPending && w.fetchStatus !== "idle"
  );

  // The last first-load error, held across an automatic refetch (see the
  // header). `retriedAt` is the errorUpdatedAt the user pressed
  // Reintentar on: that error is released and the skeleton comes back.
  const [held, setHeld] = useState<{ error: E; at: number } | null>(null);
  const [retriedAt, setRetriedAt] = useState<number | null>(null);
  if (q.isError && !hasData && held?.at !== q.errorUpdatedAt) {
    setHeld({ error: q.error, at: q.errorUpdatedAt });
  } else if (held !== null && hasData) {
    setHeld(null);
  }
  const holdingError =
    held !== null &&
    !hasData &&
    !q.isError &&
    held.at === q.errorUpdatedAt &&
    retriedAt !== held.at;

  const { refetch, errorUpdatedAt } = q;
  const retry = useCallback(() => {
    setRetriedAt(errorUpdatedAt);
    void refetch();
  }, [refetch, errorUpdatedAt]);

  const status = resolveLoadStatus({
    hasData,
    isEmpty: hasData && opts?.isEmpty ? opts.isEmpty(q.data as T) : false,
    failed: q.isError || holdingError || offline,
    pending: fetching || (disabled && upstreamPending),
  });

  const phase = useSkeletonPhase(status === "loading");
  const slow = useHeldFor(status === "loading", SKELETON_SLOW_MS);
  const revalidatingHint = useHeldFor(hasData && q.isFetching, REVALIDATING_HINT_MS);

  // A painted skeleton stays through its minimum (phase "holding") even
  // if the data already arrived — no flash-off.
  if (status === "loading" || isSkeletonVisible(phase)) {
    return { kind: "loading", visible: isSkeletonVisible(phase), slow };
  }
  switch (status) {
    case "error":
      return {
        kind: "error",
        error: q.isError ? q.error : holdingError ? held.error : null,
        offline,
        retry,
      };
    case "empty":
      return { kind: "empty" };
    case "disabled":
      return { kind: "disabled" };
    default:
      return {
        kind: "ready",
        data: q.data as T,
        refreshFailed: q.isError,
        revalidatingHint,
      };
  }
}
